// AR scan mode, DOM glue (2026-10-14, step 1 of
// reports/ar_scan_mode_spec_2026-10-14.md). The pure parts are in scanCore.ts.
//
// OFF by default and never remembered as on: every AR session starts with
// scan mode off, and it switches itself off when the camera view closes or
// the app goes to the background. Only while it is on do camera frames
// leave the phone. Each frame goes into the existing outbox (outbox.ts) as
// a multipart upload to /api/field/scans/{session}/frames, so recording
// works without signal and drains later; the server blurs faces and number
// plates before it stores anything. The session's create and stop are
// queued JSON requests in the same outbox, in order with the frames.
import { discardKind, enqueue, listOutbox, newUuid, onOutboxChange, onUploaded } from "./outbox";
import type { OutboxItem } from "./outbox";
import { DEFAULT_CADENCE, EstimateRing, ScanScheduler, WALKING_TURN_DEG, buildFrameMeta, horizontalFov, queueState, recLabel, turnCoverage } from "./scanCore";
import type { QueueLevel, ScanCadence, TurnSample } from "./scanCore";
import "../styles/scanRing.css";
import { getHeading, screenAngle } from "./heading";
import { trackEstimate } from "./track";

export interface ScanHost {
  video: () => HTMLVideoElement | null;
  /** the raw GPS fix (not the filtered one) */
  gps: () => { lat: number; lon: number; accuracy: number | null; at?: number | null } | null;
  fov: () => number;
  calibration: () => { heading_offset_deg: number; east_m: number; north_m: number } | null;
  vo: () => { on: boolean; quality: string | null } | null;
  /** state changed (indicator text, on/off) */
  onChange: () => void;
}

export interface ScanConfig {
  enabled: boolean;
  cadence: ScanCadence;
  model?: { available: boolean; reason: string };
  privacy?: string;
}

const TICK_MS = 250;

const S: {
  on: boolean;
  host: ScanHost | null;
  sid: string | null;
  projectName: string;
  startedPerf: number;
  seq: number;
  sent: number;
  queued: number;
  level: QueueLevel;
  timer: number | null;
  busy: boolean;
  lastNotify: number;
  ring: EstimateRing;
  sched: ScanScheduler;
  cfg: ScanConfig;
  error: string | null;
  /** the frames taken, for the "degrees covered" ring (row #44); hfov: their horizontal field of view */
  turn: TurnSample[];
  hfov: number;
} = {
  on: false, host: null, sid: null, projectName: "", startedPerf: 0, seq: 0, sent: 0, queued: 0, level: "ok",
  timer: null, busy: false, lastNotify: 0, ring: new EstimateRing(10), sched: new ScanScheduler(DEFAULT_CADENCE),
  cfg: { enabled: true, cadence: DEFAULT_CADENCE }, error: null, turn: [], hfov: 40,
};

let cfgLoaded = false;
/** /api/field/scans/config (cadence, caps, privacy wording, model availability). Offline: the defaults. */
export async function loadScanConfig(): Promise<ScanConfig> {
  if (cfgLoaded) return S.cfg;
  try {
    const res = await fetch("/api/field/scans/config", { credentials: "same-origin" });
    if (res.ok) {
      const d = (await res.json()) as ScanConfig;
      S.cfg = { ...d, cadence: { ...DEFAULT_CADENCE, ...(d.cadence ?? {}) } };
      cfgLoaded = true;
    } else if (res.status === 404) {
      // a GIS-view organisation has no projects: scan mode is not offered
      S.cfg = { ...S.cfg, enabled: false };
      cfgLoaded = true;
    }
  } catch {
    /* offline: defaults */
  }
  return S.cfg;
}

export function scanConfig(): ScanConfig {
  return S.cfg;
}

export function scanOn(): boolean {
  return S.on;
}

export function scanLabel(): string {
  return recLabel(performance.now() / 1000 - S.startedPerf, S.sent, S.queued, S.level);
}

export function scanStatus(): { on: boolean; sid: string | null; project: string; sent: number; queued: number; level: QueueLevel; error: string | null } {
  return { on: S.on, sid: S.sid, project: S.projectName, sent: S.sent, queued: S.queued, level: S.level, error: S.error };
}

function isScan(i: OutboxItem): boolean {
  return i.kind === "scan_frame" || i.kind === "scan_session" || i.kind === "scan_stop";
}

let countTimer: number | null = null;
async function recount(): Promise<void> {
  const items = await listOutbox();
  S.queued = items.filter((i) => i.kind === "scan_frame" && !i.failed).length;
  S.level = queueState(S.queued, S.cfg.cadence.max_queued_frames);
  const bad = items.find((i) => isScan(i) && i.failed && i.group === S.sid);
  S.error = bad ? bad.last_error : null;
  notify(true);
}
function scheduleRecount(): void {
  if (countTimer !== null) return;
  countTimer = window.setTimeout(() => {
    countTimer = null;
    void recount();
  }, 600);
}

let wired = false;
function wire(): void {
  if (wired) return;
  wired = true;
  onOutboxChange(() => {
    if (S.on) scheduleRecount();
  });
  onUploaded((_rec, item) => {
    if (item.kind === "scan_frame" && item.group === S.sid) S.sent += 1;
  });
}

function notify(force = false): void {
  const t = performance.now();
  if (!force && t - S.lastNotify < 1000) return;
  S.lastNotify = t;
  renderRing();
  S.host?.onChange();
}

// --- "turn slowly on the spot": the degrees-covered ring (row #44) ----------
//
// While recording, a ring over the camera view shows how much of the circle the frames taken HERE cover (the
// panorama the scan worker can stitch from them), with the hint to stop and turn slowly at each asset.

const SVG_NS = "http://www.w3.org/2000/svg";

function renderRing(): void {
  const host = S.host?.video()?.parentElement ?? null;
  let box = document.querySelector<HTMLElement>(".fa-scan-ring");
  if (!S.on || !host) {
    box?.remove();
    return;
  }
  if (!box || box.parentElement !== host) {
    box?.remove();
    box = document.createElement("div");
    box.className = "fa-scan-ring";
    box.setAttribute("role", "status");
    host.append(box);
  }
  const t = performance.now() / 1000;
  // "now": the last frame's view, with where the person is and whether they're walking at this moment, and
  // the live heading (so turning while walking shows before the next frame)
  const est = trackEstimate();
  const lastS = S.turn[S.turn.length - 1];
  const now = lastS ? [{ ...lastS, t, e: est?.e ?? lastS.e, n: est?.n ?? lastS.n, sigma: est?.sigma ?? lastS.sigma,
    motion: est?.motion ?? lastS.motion }] : [];
  const live = getHeading().heading;
  const cov = turnCoverage([...S.turn, ...now], S.hfov);
  const walkingTurn = cov.walkingTurn || (est?.motion === "walking" && live !== null && lastS?.heading != null
    && Math.abs((((live - lastS.heading) + 540) % 360) - 180) >= WALKING_TURN_DEG);
  const r = 26, c = 2 * Math.PI * r;
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", "0 0 64 64");
  svg.setAttribute("width", "64");
  svg.setAttribute("height", "64");
  svg.setAttribute("aria-hidden", "true");
  const circle = (cls: string, from: number, to: number) => {
    const e = document.createElementNS(SVG_NS, "circle");
    e.setAttribute("cx", "32");
    e.setAttribute("cy", "32");
    e.setAttribute("r", String(r));
    e.setAttribute("class", cls);
    e.setAttribute("stroke-dasharray", `${(((to - from) / 360) * c).toFixed(1)} ${c.toFixed(1)}`);
    e.setAttribute("transform", `rotate(${(from - 90).toFixed(1)} 32 32)`);
    svg.append(e);
  };
  circle("fa-ring-all", 0, 360);
  for (const [a, b] of cov.arcs) circle("fa-ring-got", a, b);
  const full = cov.deg >= 350;
  const text = document.createElement("div");
  text.className = "fa-ring-text";
  const big = document.createElement("strong");
  big.textContent = full ? "Full circle ✓" : `${cov.deg}° of 360°`;
  const hint = document.createElement("span");
  // truthful (row #52): only frames taken standing still count, so say so when someone turns on the move
  hint.textContent = walkingTurn ? "Stand still while you turn"
    : full ? "Walk on to the next asset" : "At each asset: stop and turn slowly on the spot";
  box.classList.toggle("fa-ring-walking", walkingTurn);
  text.append(big, hint);
  box.replaceChildren(svg, text);
  box.setAttribute("aria-label", walkingTurn ? `${cov.deg} of 360 degrees covered here. Stand still while you turn: frames taken while walking don't count.`
    : full ? "Full circle covered here" : `${cov.deg} of 360 degrees covered here. Stop and turn slowly on the spot.`);
}

/** What a recording is stored against (2026-10-15): a project, or a named
 * general site. Either way it joins an organisation-shared site: the one
 * picked from the nearby suggestions (`siteId`), else the server matches a
 * nearby site with a similar name, unless `newSite` ("start a fresh site"). */
export interface ScanTarget {
  kind: "project" | "site";
  project?: { id: string; name: string } | null;
  siteId?: number | null;
  siteName?: string | null;
  siteLabel?: string | null;
  newSite?: boolean;
  /** "auto": as-built when the project is in construction (server side) */
  captureType?: "auto" | "normal" | "as_built";
}

/** Start recording into a new session. */
export function startScan(host: ScanHost, target: ScanTarget): string {
  const project = target.project ?? null;
  const title = target.kind === "project" && project ? project.name : (target.siteName || target.siteLabel || "Site scan");
  if (S.on) stopScan();
  wire();
  const sid = newUuid();
  const gps = host.gps();
  S.on = true;
  S.host = host;
  S.sid = sid;
  S.projectName = title;
  S.startedPerf = performance.now() / 1000;
  S.seq = 0;
  S.sent = 0;
  S.queued = 0;
  S.level = "ok";
  S.error = null;
  S.ring.clear();
  S.turn = [];
  S.sched = new ScanScheduler(S.cfg.cadence);
  const body: Record<string, unknown> = {
    client_uuid: sid, kind: target.kind, project_id: target.kind === "project" ? project?.id ?? null : null,
    site_id: target.siteId ?? null, site_name: target.kind === "site" ? target.siteName || null : null,
    site_label: target.siteLabel || null, new_site: !!target.newSite, capture_type: target.captureType ?? "auto",
    started_at: new Date().toISOString(),
    lat: gps?.lat ?? null, lon: gps?.lon ?? null, accuracy_m: gps?.accuracy ?? null, device: navigator.userAgent.slice(0, 300),
  };
  sessionBody = body;
  void enqueue({ client_uuid: sid, meta: { label: `Scan started · ${title}` }, photos: [], photo_headings: [],
    endpoint: "/api/field/scans", method: "POST", json: body, kind: "scan_session", group: sid });
  S.timer = window.setInterval(tick, TICK_MS);
  notify(true);
  return sid;
}

let sessionBody: Record<string, unknown> | null = null;

/** Stop recording (the queued frames still upload). */
export function stopScan(): void {
  if (!S.on) return;
  const sid = S.sid;
  S.on = false;
  if (S.timer !== null) window.clearInterval(S.timer);
  S.timer = null;
  if (sid) {
    void enqueue({ client_uuid: `${sid}-stop`, meta: { label: "Scan stopped" }, photos: [], photo_headings: [],
      endpoint: `/api/field/scans/${sid}/stop`, method: "POST",
      json: { ended_at: new Date().toISOString(), frames_sent: S.seq }, kind: "scan_stop", group: sid });
  }
  S.turn = [];
  renderRing();
  notify(true);
}

/** Throw away every scan frame still waiting on this phone. */
export async function discardQueuedScans(): Promise<number> {
  let n = 0;
  for (const k of ["scan_frame", "scan_session", "scan_stop"]) n += await discardKind(k);
  await recount();
  return n;
}

function tick(): void {
  if (!S.on || !S.host) return;
  const est = trackEstimate();
  if (est) S.ring.push({ t: est.t, e: est.e, n: est.n, sigma: est.sigma });
  notify();
  if (S.busy || S.level === "full") return;
  const t = performance.now() / 1000;
  const hd = getHeading();
  const sample = { t, e: est?.e ?? null, n: est?.n ?? null, heading: hd.heading };
  const reason = S.sched.due(sample);
  if (!reason) return;
  const video = S.host.video();
  if (!video || !video.videoWidth) return;
  S.sched.taken(sample);
  S.busy = true;
  void capture(video, reason, t).finally(() => {
    S.busy = false;
  });
}

async function capture(video: HTMLVideoElement, reason: ReturnType<ScanScheduler["due"]> & string, tPerf: number): Promise<void> {
  const host = S.host;
  const sid = S.sid;
  if (!host || !sid) return;
  // everything describing the moment of capture is read BEFORE the (async) encode
  const tMs = Date.now();
  const hd = getHeading();
  const est = trackEstimate();
  const gps = host.gps();
  const cad = S.cfg.cadence;
  const s = Math.min(1, cad.long_edge_px / Math.max(video.videoWidth, video.videoHeight));
  const c = document.createElement("canvas");
  c.width = Math.round(video.videoWidth * s);
  c.height = Math.round(video.videoHeight * s);
  c.getContext("2d")?.drawImage(video, 0, 0, c.width, c.height);
  const blob = await new Promise<Blob | null>((res) => c.toBlob((b) => res(b), "image/jpeg", cad.jpeg_quality));
  if (!blob || !S.on || S.sid !== sid) return;
  const seq = S.seq++;
  const meta = buildFrameMeta({
    clientUuid: newUuid(), seq, tMs, tPerf, reason,
    gps,
    pose: { heading: hd.heading, heading_accuracy: hd.accuracy, pitch: hd.pitch, roll: hd.roll, source: hd.source,
      disturbed: hd.disturbed, screen_angle: screenAngle() },
    estimate: est ? { lat: est.lat, lon: est.lon, sigma: est.sigma, e: est.e, n: est.n, origin_lat: est.originLat,
      origin_lon: est.originLon, held: est.held, motion: est.motion } : null,
    camera: { fov_deg: host.fov(), width: c.width, height: c.height, video_width: video.videoWidth, video_height: video.videoHeight },
    calibration: host.calibration(),
    vo: host.vo(),
    session: sessionBody && sessionBody.client_uuid === sid ? sessionBody : null,
  });
  await enqueue({ client_uuid: String(meta.client_uuid), meta, photos: [blob], photo_headings: [],
    upload_endpoint: `/api/field/scans/${sid}/frames`, file_field: "frame", kind: "scan_frame", group: sid });
  S.hfov = horizontalFov(host.fov(), c.width, c.height);
  S.turn.push({ t: tPerf, heading: hd.heading, e: est?.e ?? null, n: est?.n ?? null, sigma: est?.sigma ?? null,
    motion: est?.motion ?? null });
  if (S.turn.length > 240) S.turn.splice(0, S.turn.length - 240);
  notify(true);
}

// --- the project's scan sessions (field app → Projects → a project) ---------

interface ScanSessionRow {
  client_uuid: string;
  site_id?: number | null;
  site_name?: string | null;
  capture_type?: string;
  site_label: string | null;
  status: string;
  received_at: string;
  n_frames: number;
  n_faces: number;
  n_plates: number;
  retention?: { until: string | null; rule: string };
}

/** The project's scan sessions; an admin also gets "Delete scan session"
 * (a reason is required; the server deletes the frames, keeps a tombstone
 * and an audit entry). */
export function scanSessionsEl(projectId: string, isAdmin: boolean): HTMLElement {
  const box = document.createElement("div");
  box.className = "fa-scan-sessions";
  const load = async (): Promise<void> => {
    let rows: ScanSessionRow[] = [];
    try {
      const res = await fetch(`/api/field/scans?project_id=${encodeURIComponent(projectId)}`, { credentials: "same-origin" });
      if (!res.ok) {
        box.replaceChildren();
        return;
      }
      rows = (await res.json()) as ScanSessionRow[];
    } catch {
      box.replaceChildren();
      return;
    }
    const head = document.createElement("h3");
    head.textContent = `Scans (${rows.length})`;
    const list = document.createElement("ul");
    list.className = "fa-list";
    for (const r of rows) {
      const li = document.createElement("li");
      li.className = "fa-cand";
      const main = document.createElement("span");
      main.className = "fa-cand-main";
      const t = document.createElement("strong");
      t.textContent = `${r.capture_type === "as_built" ? "As-built · " : ""}${r.site_label || r.site_name || "Scan"} · ${r.received_at.slice(0, 10)}`;
      const s = document.createElement("small");
      s.textContent = `${r.n_frames} frame(s) · ${r.n_faces} face(s), ${r.n_plates} plate(s) blurred · ${r.status}` +
        (r.retention ? ` · ${r.retention.until ? `kept until ${r.retention.until.slice(0, 10)}` : r.retention.rule}` : "");
      main.append(t, s);
      li.append(main);
      if (r.site_id) {
        const view = document.createElement("button");
        view.className = "fa-link";
        view.textContent = "View";
        view.addEventListener("click", () => void import("../scanViewer").then((m) => m.openSiteViewer(r.site_id as number, { session: r.client_uuid })));
        li.append(view);
      }
      if (isAdmin) {
        const del = document.createElement("button");
        del.className = "fa-link";
        del.textContent = "Delete scan session";
        del.addEventListener("click", () => void (async () => {
          const reason = window.prompt(`Delete this scan (${r.n_frames} frames)? This can't be undone. Reason:`);
          if (reason === null) return;
          if (reason.trim().length < 3) {
            window.alert("A reason is needed to delete a scan.");
            return;
          }
          const res = await fetch(`/api/field/scans/${encodeURIComponent(r.client_uuid)}`, {
            method: "DELETE", credentials: "same-origin", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ reason: reason.trim() }),
          });
          if (!res.ok) {
            const d = await res.json().catch(() => null);
            window.alert(`Not deleted: ${d?.detail ?? res.status}`);
            return;
          }
          await load();
        })());
        li.append(del);
      }
      list.append(li);
    }
    box.replaceChildren(...(rows.length ? [head, list] : []));
  };
  void load();
  return box;
}
